/**
 * A tiny key–value wrapper over IndexedDB for what has to survive the app
 * being closed with no signal: changes waiting to sync (with photos as
 * Blobs) and the checks last opened. Every call fails soft — private
 * browsing or blocked storage just means nothing is kept.
 */

const DB_NAME = 'mustertap-offline';
const VERSION = 1;
export type StoreName = 'outbox' | 'cache';

let dbPromise: Promise<IDBDatabase | null> | undefined;

function open(): Promise<IDBDatabase | null> {
  dbPromise ??= new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        for (const name of ['outbox', 'cache']) {
          if (!req.result.objectStoreNames.contains(name)) req.result.createObjectStore(name);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function run<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return open().then((db) => new Promise<T | undefined>((resolve) => {
    if (!db) return resolve(undefined);
    try {
      const tx = db.transaction(store, mode);
      const req = fn(tx.objectStore(store));
      tx.oncomplete = () => resolve(req ? req.result : undefined);
      tx.onerror = () => resolve(undefined);
      tx.onabort = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  }));
}

export const idbGet = <T>(store: StoreName, key: string) => run<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>);
export const idbSet = (store: StoreName, key: string, value: unknown) => run(store, 'readwrite', (s) => { s.put(value, key); });
export const idbDelete = (store: StoreName, key: string) => run(store, 'readwrite', (s) => { s.delete(key); });
export const idbClear = (store: StoreName) => run(store, 'readwrite', (s) => { s.clear(); });

export async function idbAll<T>(store: StoreName): Promise<T[]> {
  return (await run<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>)) ?? [];
}
